/**
 * SkillClient — Skill Marketplace API client with SigV4 auth.
 *
 * Uses @amzn/cmhk-agent-platform-service-client (generated Smithy TS Client)
 * to call CMHKAgentPlatformService REST API directly via SigV4.
 *
 * Auth flow:
 *   Federate id_token → Cognito Identity Pool → IAM temp credentials → SigV4
 *
 * Usage:
 *   import { getSkillClient, ListCatalogCommand } from '../client/SkillClient';
 *   const result = await getSkillClient().send(new ListCatalogCommand({ agentName: 'data_ops_agent' }));
 */

import { AgentPlatformClient, ListMemoryCommand } from '@amzn/cmhk-agent-platform-service-client';
import type { ListMemoryCommandInput, MemoryRecord } from '@amzn/cmhk-agent-platform-service-client';
import { fromCognitoIdentityPool } from '@aws-sdk/credential-provider-cognito-identity';
import { HttpRequest } from '@smithy/protocol-http';
import { appConfig } from '../config';
import { getIdToken } from '../context/AuthContext';

// ── Re-export commands & types for convenience ───────────────────────────────

export {
  AgentPlatformClient,
  ListCatalogCommand,
  GetCatalogItemCommand,
  ListEquipmentCommand,
  EquipSkillCommand,
  UnequipSkillCommand,
  ListTagsCommand,
  ListMemoryCommand,
} from '@amzn/cmhk-agent-platform-service-client';

export type {
  CatalogItem,
  EquipmentItem,
  TagItem,
  ListCatalogCommandInput,
  ListCatalogCommandOutput,
  GetCatalogItemCommandInput,
  GetCatalogItemCommandOutput,
  ListEquipmentCommandInput,
  ListEquipmentCommandOutput,
  EquipSkillCommandInput,
  EquipSkillCommandOutput,
  UnequipSkillCommandInput,
  UnequipSkillCommandOutput,
  MemoryRecord,
  MemoryType,
  ListMemoryCommandInput,
  ListMemoryCommandOutput,
} from '@amzn/cmhk-agent-platform-service-client';

// ── Credential provider ──────────────────────────────────────────────────────

function createCredentialsProvider() {
  return fromCognitoIdentityPool({
    identityPoolId: appConfig.identityPoolId,
    logins: {
      [appConfig.identityProvider]: () => {
        const token = getIdToken();
        if (!token) throw new Error('No id_token available');
        return Promise.resolve(token);
      },
    },
    clientConfig: { region: appConfig.region },
  });
}

// ── Client singleton ─────────────────────────────────────────────────────────

let _client: AgentPlatformClient | null = null;

export function getSkillClient(): AgentPlatformClient {
  if (!_client) {
    _client = new AgentPlatformClient({
      endpoint: appConfig.skillApiEndpoint,
      region: appConfig.region,
      credentials: createCredentialsProvider(),
    });
    // Forward the caller identity on every Agent Platform request. The build
    // step runs before SigV4 signing, so the token is covered by the signature.
    _client.middlewareStack.add(
      (next) => async (args) => {
        if (HttpRequest.isInstance(args.request)) {
          const token = getIdToken();
          if (!token) throw new Error('No id_token available');
          args.request.headers['X-Id-Token'] = token;
        }
        return next(args);
      },
      { step: 'build', name: 'skillIdTokenHeader', priority: 'high' },
    );
  }
  return _client;
}

/**
 * Reset the client (e.g. after re-login when credentials expire).
 * Next call to getSkillClient() will create a fresh instance.
 */
export function resetSkillClient(): void {
  if (_client) {
    _client.destroy();
    _client = null;
  }
  if (_s3Client) {
    _s3Client.destroy();
    _s3Client = null;
  }
}

// ── Memory (Account Settings → Memory tab) ───────────────────────────────────

/**
 * List a user's persistent memory records (DAT-8558 ListMemory).
 *
 * Thin wrapper over getSkillClient().send(ListMemoryCommand) — hits the same
 * CMHKAgentPlatformService endpoint with the same Cognito → SigV4 auth as the
 * Skill Marketplace. `userAlias` and `agentName` are required by the API.
 * Always returns a concrete array so callers don't have to null-check `items`.
 */
export async function listMemory(
  input: ListMemoryCommandInput,
): Promise<{ items: MemoryRecord[]; nextToken?: string }> {
  const out = await getSkillClient().send(new ListMemoryCommand(input));
  return { items: out.items ?? [], nextToken: out.nextToken };
}

// ── S3 presigned URL for skill thumbnails ────────────────────────────────────

import { S3Client, GetObjectCommand } from '@aws-sdk/client-s3';
import { getSignedUrl } from '@aws-sdk/s3-request-presigner';

let _s3Client: S3Client | null = null;

function getS3Client(): S3Client {
  if (!_s3Client) {
    _s3Client = new S3Client({
      region: appConfig.region,
      credentials: createCredentialsProvider(),
    });
  }
  return _s3Client;
}

/**
 * Parse an S3 URI (s3://bucket/key) into bucket and key.
 */
function parseS3Uri(uri: string): { bucket: string; key: string } | null {
  const match = uri.match(/^s3:\/\/([^/]+)\/(.+)$/);
  return match ? { bucket: match[1], key: match[2] } : null;
}

/**
 * Generate a presigned URL for a skill thumbnail.
 * Derives the thumbnail path from s3_location by replacing the filename with preview.jpg.
 */
export async function getSkillThumbnailUrl(s3Location: string): Promise<string | null> {
  const parsed = parseS3Uri(s3Location);
  if (!parsed) return null;

  const dir = parsed.key.substring(0, parsed.key.lastIndexOf('/') + 1);
  const thumbnailKey = dir + 'preview.jpg';

  try {
    return await getSignedUrl(
      getS3Client(),
      new GetObjectCommand({ Bucket: parsed.bucket, Key: thumbnailKey }),
      { expiresIn: 3600 },
    );
  } catch {
    return null;
  }
}
